import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, ChevronDown, Phone, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { SERVICES, SERVICE_CATEGORIES, ICON_MAP, SERVICE_COLORS } from '@/data/servicesData';

// Group services by category for menus
const SERVICE_CAT_LABELS = { conseil: 'Conseil & Stratégie', marketing: 'Marketing', brand: 'Brand Solutions', digital: 'Digital & IA' };
const SERVICE_CATS_ORDER = ['conseil', 'marketing', 'brand', 'digital'];
const SERVICES_BY_CAT = SERVICE_CATS_ORDER.map(cat => ({
  key: cat,
  label: SERVICE_CAT_LABELS[cat],
  items: SERVICES.filter(s => s.category === cat),
}));

const NAV_ITEMS = [
  {
    label: "L'agence", path: '/agence',
    children: [
      { label: 'Qui sommes-nous ?', path: '/agence#qui-sommes-nous' },
      { label: 'Partenaires', path: '/partenaires' },
    ]
  },
  {
    label: 'Services', path: '/services',
    isMegaMenu: true,
  },
  { label: 'Formations', path: '/formations' },
  { label: 'Clients', path: '/clients' },
  {
    label: 'Ressources', path: '/ressources',
    children: [
      { label: 'Blog expert', path: '/blog' },
      { label: 'Ressources', path: '/ressources/telechargements' },
    ]
  },
];

function ServicesMegaMenu({ open, onClose }) {
  if (!open) return null;
  return (
    <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50 w-[700px]" role="menu">
      <div className="bg-white rounded-[5px] p-5 shadow-[0_8px_30px_rgba(3,34,141,0.15)] border border-gray-100">
        <div className="grid grid-cols-2 gap-x-8 gap-y-5">
          {SERVICES_BY_CAT.map((cat) => (
            <div key={cat.key}>
              <p className="text-[10px] font-cabin font-semibold uppercase tracking-[0.15em] text-[#03228D]/40 mb-2 px-1">{cat.label}</p>
              <div className="space-y-0.5">
                {cat.items.map((svc) => {
                  const Icon = ICON_MAP[svc.icon];
                  const color = SERVICE_COLORS[svc.id] || '#03228D';
                  return (
                    <Link
                      key={svc.slug}
                      to={`/services/${svc.slug}`}
                      role="menuitem"
                      data-testid={`nav-svc-${svc.slug}`}
                      className="flex items-center gap-2.5 px-2 py-2 rounded-[5px] text-[13px] text-[#141C39]/70 hover:text-[#03228D] hover:bg-[#F5F7FA] transition-colors font-dm group"
                      onClick={onClose}
                    >
                      {Icon && <Icon className="w-3.5 h-3.5 shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" style={{ color }} />}
                      {svc.title}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-3 border-t border-gray-100 text-center">
          <Link to="/services" onClick={onClose} className="text-xs font-cabin font-semibold uppercase tracking-wide text-[#03228D] hover:text-[#FF8C4B] transition-colors">
            Voir tous nos services →
          </Link>
        </div>
      </div>
    </div>
  );
}

function NavDropdown({ item }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isActive = location.pathname.startsWith(item.path);

  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <Link
        to={item.path}
        data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, '')}`}
        aria-expanded={item.children || item.isMegaMenu ? open : undefined}
        aria-haspopup={item.children || item.isMegaMenu ? 'true' : undefined}
        className={`flex items-center gap-1 text-sm font-semibold tracking-wide transition-colors font-cabin uppercase ${isActive ? 'text-[#FF8C4B]' : 'text-white/80 hover:text-[#FF8C4B]'}`}
      >
        {item.label}
        {(item.children || item.isMegaMenu) && <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />}
      </Link>
      {item.isMegaMenu && <ServicesMegaMenu open={open} onClose={() => setOpen(false)} />}
      {item.children && !item.isMegaMenu && open && (
        <div className="absolute top-full left-0 pt-2 z-50 min-w-[240px]" role="menu">
          <div className="bg-white rounded-[5px] p-2 shadow-[0_8px_30px_rgba(3,34,141,0.15)] border border-gray-100">
            {item.children.map((child) => (
              <Link
                key={child.path}
                to={child.path}
                role="menuitem"
                data-testid={`nav-sub-${child.label.toLowerCase().replace(/[^a-z]/g, '').slice(0,20)}`}
                className="block px-4 py-2.5 text-sm text-[#141C39]/70 hover:text-[#03228D] hover:bg-[#F5F7FA] rounded-[5px] transition-colors font-dm"
                onClick={() => setOpen(false)}
              >
                {child.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MobileNav() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [expandedCat, setExpandedCat] = useState(null);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button data-testid="mobile-menu-toggle" aria-label="Ouvrir le menu" className="lg:hidden text-white p-2">
          <Menu className="w-6 h-6" />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="bg-white border-r border-gray-100 w-[300px] p-0 overflow-y-auto">
        <div className="p-6">
          <img src="/logo_blue.webp" alt="Lazuli Marketing — Agence marketing B2B" className="h-10 mb-8" />
          <nav aria-label="Navigation mobile" className="space-y-1">
            {NAV_ITEMS.map((item) => (
              <div key={item.label}>
                {/* Services mega menu on mobile */}
                {item.isMegaMenu ? (
                  <>
                    <button
                      className="flex items-center justify-between w-full px-3 py-3 text-[#141C39]/80 hover:text-[#03228D] text-sm font-semibold font-cabin uppercase tracking-wide"
                      onClick={() => setExpanded(expanded === item.label ? null : item.label)}
                    >
                      {item.label}
                      <ChevronDown className={`w-4 h-4 transition-transform ${expanded === item.label ? 'rotate-180' : ''}`} />
                    </button>
                    {expanded === item.label && (
                      <div className="pl-2 mb-2 space-y-2">
                        {SERVICES_BY_CAT.map((cat) => (
                          <div key={cat.key}>
                            <button
                              className="flex items-center justify-between w-full px-3 py-2 text-[11px] font-cabin font-semibold uppercase tracking-[0.12em] text-[#03228D]/50 hover:text-[#03228D]"
                              onClick={() => setExpandedCat(expandedCat === cat.key ? null : cat.key)}
                            >
                              {cat.label}
                              <ChevronDown className={`w-3 h-3 transition-transform ${expandedCat === cat.key ? 'rotate-180' : ''}`} />
                            </button>
                            {expandedCat === cat.key && (
                              <div className="pl-3 space-y-0.5 mb-1">
                                {cat.items.map((svc) => (
                                  <Link key={svc.slug} to={`/services/${svc.slug}`} className="block px-3 py-1.5 text-sm text-[#141C39]/50 hover:text-[#03228D] font-dm" onClick={() => setOpen(false)}>
                                    {svc.title}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                        <Link to="/services" className="block px-3 py-2 text-xs font-cabin font-semibold text-[#03228D] hover:text-[#FF8C4B]" onClick={() => setOpen(false)}>
                          Voir tous les services →
                        </Link>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <button
                      className="flex items-center justify-between w-full px-3 py-3 text-[#141C39]/80 hover:text-[#03228D] text-sm font-semibold font-cabin uppercase tracking-wide"
                      onClick={() => item.children ? setExpanded(expanded === item.label ? null : item.label) : setOpen(false)}
                    >
                      {!item.children ? (
                        <Link to={item.path} onClick={() => setOpen(false)} className="w-full text-left">{item.label}</Link>
                      ) : (
                        <>{item.label}<ChevronDown className={`w-4 h-4 transition-transform ${expanded === item.label ? 'rotate-180' : ''}`} /></>
                      )}
                    </button>
                    {item.children && expanded === item.label && (
                      <div className="pl-4 space-y-1 mb-2">
                        {item.children.map((child) => (
                          <Link key={child.path} to={child.path} className="block px-3 py-2 text-sm text-[#141C39]/50 hover:text-[#03228D] font-dm" onClick={() => setOpen(false)}>
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </nav>
          <div className="mt-8 space-y-3">
            <Link to="/contact" onClick={() => setOpen(false)}>
              <Button data-testid="mobile-contact-btn" className="w-full bg-[#03228D] hover:bg-[#141C39] text-white rounded-[5px]">
                <Phone className="w-4 h-4 mr-2" /> Nous contacter
              </Button>
            </Link>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      role="banner"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#03228D] shadow-[0_2px_20px_rgba(3,34,141,0.25)]' : 'bg-[#03228D]'}`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16 lg:h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 shrink-0">
          <img src="/logo_white.webp" alt="Lazuli Marketing" className="h-10 lg:h-14 w-auto" width="400" height="287" />
        </Link>

        <nav aria-label="Navigation principale" className="hidden lg:flex items-center gap-6 xl:gap-8">
          {NAV_ITEMS.map((item) => (
            <NavDropdown key={item.label} item={item} />
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/contact?rdv=true">
              <Button data-testid="nav-rdv-btn" variant="ghost" className="text-white/60 hover:text-[#FF8C4B] hover:bg-white/10 text-sm font-cabin uppercase tracking-wide rounded-[5px]">
                <CalendarDays className="w-4 h-4 mr-2" /> RDV
              </Button>
            </Link>
            <Link to="/contact">
              <Button data-testid="nav-contact-btn" className="bg-white text-[#03228D] hover:bg-[#FFF3DB] shadow-lg rounded-[5px] btn-shine font-cabin uppercase tracking-wide text-sm">
                Nous contacter
              </Button>
            </Link>
          </div>
          <MobileNav />
        </div>
      </div>
    </header>
  );
}

export function Footer() {
  const services = SERVICES.map(s => ({ label: s.title, slug: s.slug }));

  return (
    <footer data-testid="main-footer" role="contentinfo" aria-label="Pied de page" className="bg-[#141C39]">
      <div className="max-w-7xl mx-auto px-6 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12">
          {/* Col 1 - Logo + description */}
          <div className="lg:col-span-3">
            <img src="/logo_white.webp" alt="Lazuli Marketing" className="h-16 w-auto mb-6" width="400" height="287" loading="lazy" />
            <p className="text-white/50 text-sm leading-relaxed font-dm">
              Depuis 2017, Lazuli Marketing accompagne les entreprises B2B dans leur stratégie marketing, leur branding et leur transformation digitale. De la startup à la grande entreprise, nous intervenons dans toute la France avec des bureaux à Lyon et Paris.
            </p>
          </div>

          {/* Col 2 - Services (split in 2) */}
          <div className="lg:col-span-5">
            <h3 className="font-cabin font-semibold text-white text-sm uppercase tracking-wider mb-4">Nos expertises</h3>
            <div className="grid grid-cols-2 gap-x-6">
              <ul className="space-y-2">
                {services.slice(0, 7).map(s => (
                  <li key={s.slug}><Link to={`/services/${s.slug}`} className="text-[13px] text-white/50 hover:text-[#FF8C4B] transition-colors font-dm leading-tight">{s.label}</Link></li>
                ))}
              </ul>
              <ul className="space-y-2">
                {services.slice(7).map(s => (
                  <li key={s.slug}><Link to={`/services/${s.slug}`} className="text-[13px] text-white/50 hover:text-[#FF8C4B] transition-colors font-dm leading-tight">{s.label}</Link></li>
                ))}
              </ul>
            </div>
          </div>

          {/* Col 3 - L'agence */}
          <div className="lg:col-span-2">
            <h3 className="font-cabin font-semibold text-white text-sm uppercase tracking-wider mb-4">L'agence</h3>
            <ul className="space-y-2.5">
              {[
                { label: 'Qui sommes-nous', path: '/agence' },
                { label: 'Formations', path: '/formations' },
                { label: 'Clients', path: '/clients' },
                { label: 'Partenaires', path: '/partenaires' },
                { label: 'Blog', path: '/blog' },
                { label: 'Ressources', path: '/ressources' },
                { label: 'Contact', path: '/contact' },
              ].map(l => (
                <li key={l.label}><Link to={l.path} className="text-[13px] text-white/50 hover:text-[#FF8C4B] transition-colors font-dm">{l.label}</Link></li>
              ))}
            </ul>
          </div>

          {/* Col 4 - Contact Lyon + Paris */}
          <div className="lg:col-span-2">
            <h3 className="font-cabin font-semibold text-white text-sm uppercase tracking-wider mb-4">Contact</h3>
            <div className="space-y-4">
              <ul className="space-y-1 text-[13px] text-white/50 font-dm">
                <li>112 bd des Belges, 69006 Lyon</li>
              </ul>
              <ul className="space-y-1 text-[13px] text-white/50 font-dm">
                <li>70 rue du Rendez-vous, 75012 Paris</li>
              </ul>
              <p className="text-[13px] text-white/50 font-dm mt-2">09 72 16 60 63</p>
            </div>
            <Link to="/contact">
              <Button data-testid="footer-contact-btn" className="mt-6 bg-[#03228D] hover:bg-[#254EDB] text-white rounded-[5px] text-sm">Nous contacter</Button>
            </Link>
            <a href="https://www.linkedin.com/company/lazuli-marketing/" target="_blank" rel="noopener noreferrer" data-testid="footer-linkedin" className="mt-3 inline-flex items-center gap-2 text-[13px] text-white/40 hover:text-[#FF8C4B] transition-colors font-dm">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              LinkedIn
            </a>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-white/25 font-dm">&copy; {new Date().getFullYear()} Lazuli Marketing. Tous droits réservés.</p>
          <div className="flex items-center gap-6 flex-wrap justify-center">
            <Link to="/mentions-legales" className="text-xs text-white/25 hover:text-[#FF8C4B] transition-colors font-dm">Mentions légales</Link>
            <Link to="/politique-de-confidentialite" className="text-xs text-white/25 hover:text-[#FF8C4B] transition-colors font-dm">Politique de confidentialité</Link>
            <a href="https://www.datacomplyone.eu" target="_blank" rel="noopener noreferrer" data-testid="footer-dco-badge" className="inline-flex items-center gap-1.5 text-xs text-[#2A9D8F] hover:text-[#FF8C4B] transition-colors font-dm">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>
              Conformité RGPD par Data Comply One
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen bg-white">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:bg-[#03228D] focus:text-white focus:px-4 focus:py-2 focus:rounded-[5px] focus:text-sm focus:font-cabin">
        Aller au contenu principal
      </a>
      <Navbar />
      <main id="main-content">{children}</main>
      <Footer />
    </div>
  );
}
